import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Banner, BlockStack, Box, Card, IndexTable, InlineGrid, InlineStack, List, Pagination, Spinner, Text, Thumbnail, useIndexResourceState } from '@shopify/polaris';
import { ImageIcon } from '@shopify/polaris-icons';
import { cacApi } from './apiClient';
import { CertaintyPill, StatusBadge, fullPath } from './CertaintyPill';
import CategoryPicker from './CategoryPicker';
import ReviewFilters from './ReviewFilters';
import WriteModal from './WriteModal';
import useQueryState from './useQueryState';
import { PER_PAGE, productPath } from './routes';
import { reviewChips } from './facetUtils';

import { queuedText, skippedText } from './analyzeText';

const AI_LABELS = { image: 'image', lookup: 'vendor page', category: 'category pick' };
// Which paid layers answered for the product: the Review list's AI column (Eric, 2026-10-28: avoid processing twice).
export function aiText(layers) {
  return (layers || []).map((l) => AI_LABELS[l] || l).join(', ');
}
const agoShort = (iso) => {
  const t = iso ? new Date(iso).getTime() : NaN;
  if (Number.isNaN(t)) return '';
  const h = Math.round((Date.now() - t) / 3600000);
  return h < 1 ? 'just now' : h < 24 ? `${h} h ago` : `${Math.round(h / 24)} d ago`;
};
export function aiStateText(r) {
  if (r.ai_state === 'done') return aiText(r.ai_layers);
  if (r.ai_state === 'checked') return 'checked, nothing to ask';
  if (r.ai_state === 'failed') return 'tried, no answer';
  return '';
}
function aiBadge(r) {
  if (!r.ai_state || r.ai_state === 'none') return <Text as="span" tone="subdued">—</Text>;
  const tone = r.ai_state === 'done' ? 'success' : r.ai_state === 'failed' ? 'critical' : 'info';
  return (
    <BlockStack gap="050">
      <Badge tone={tone}>{aiStateText(r)}</Badge>
      {r.ai_at && <Text as="span" variant="bodySm" tone="subdued">{agoShort(r.ai_at)}</Text>}
    </BlockStack>
  );
}

const ANALYZED = ['proposed', 'edited', 'approved', 'written', 'failed'];
// Heading index -> API sort column (the Polaris sort index counts headings, not the checkbox).
const SORT_COLS = ['title', 'vendor', 'category_changed', null, null, 'conflict_count', 'certainty', 'review_status', 'updated_at'];
const updated = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/New_York' }) : '');

// "Showing the products analyzed in run …": the Runs tab links here with ?run=<id>.
function RunBanner({ runId, total, onClear }) {
  const [run, setRun] = useState(null);
  useEffect(() => { let on = true; cacApi.runs().then((r) => { if (on) setRun((r.runs || []).find((x) => String(x.id) === String(runId)) || null); }).catch(() => {}); return () => { on = false; }; }, [runId]);
  const when = run && (run.started_at || run.queued_at) ? new Date(run.started_at || run.queued_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' }) : '';
  return (
    <Banner tone="info" onDismiss={onClear} title={`Products from one run${run ? `: ${run.kind} · ${when}` : ''}`}>
      <p>{total.toLocaleString()} product{total === 1 ? '' : 's'} got a proposal in this run{run && run.note ? ` (${run.note})` : ''}. Dismiss to see every product again.</p>
    </Banner>
  );
}

export default function ReviewTab({ notify, dictionary }) {
  const navigate = useNavigate();
  const [state, set, search] = useQueryState();
  const [data, setData] = useState({ rows: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refused, setRefused] = useState(null);       // 409 body: { detail, ids }
  const [writing, setWriting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const seen = useRef(new Map());                      // product_id -> row, for selections that outlive a page

  const params = useMemo(() => ({
    q: state.q, sort: state.sort, dir: state.dir, page: state.page, per_page: PER_PAGE,
    certainty_min: state.certainty_min, certainty_max: state.certainty_max,
    category_changed: state.category_changed, has_conflict: state.has_conflict,
    facet: state.facet, vendor: state.vendor, status: state.status ? [state.status] : ANALYZED, ai: state.ai, image: state.image, run_id: state.run,
  }), [state]);

  useEffect(() => {
    let on = true;
    setLoading(true); setError('');
    cacApi.products(params).then((d) => {
      if (!on) return;
      d.rows.forEach((r) => seen.current.set(r.product_id, r));
      setData(d); setLoading(false);
    }).catch((e) => { if (on) { setError(e.message); setLoading(false); } });
    return () => { on = false; };
  }, [params, tick]);
  const reload = useCallback(() => setTick((t) => t + 1), []);

  const { selectedResources, allResourcesSelected, handleSelectionChange, clearSelection } =
    useIndexResourceState(data.rows, { resourceIDResolver: (r) => String(r.product_id) });
  const ids = selectedResources.map(Number);
  const titleOf = (id) => (seen.current.get(Number(id)) || {}).title || `Product ${id}`;
  const allApproved = ids.length > 0 && ids.every((id) => (seen.current.get(id) || {}).review_status === 'approved');

  const act = async (fn, okText) => {
    setBusy(true); setRefused(null);
    try { const r = await fn(); notify(okText(r)); clearSelection(); reload(); }
    catch (e) {
      if (e.status === 409 && e.productIds) setRefused({ detail: e.message, ids: e.productIds }); else notify(e.message, true);
    }
    setBusy(false);
  };
  const [pickingCategory, setPickingCategory] = useState(false);
  const pickCategory = (h) => {
    setPickingCategory(false);
    act(() => cacApi.setCategory(ids, `${h.id}|${h.name}`), (r) => `Changed category on ${r.products} products to ${String(h.name).split(' > ').pop()}`);
  };
  const bulkActions = [
    { content: 'Change category…', onAction: () => setPickingCategory(true) },
    { content: 'Approve', onAction: () => act(() => cacApi.approve(ids), (r) => `Approved ${r.approved} products`) },
    { content: 'Approve as-is', onAction: () => act(() => cacApi.approve(ids, true), (r) => `Approved ${r.approved} products as-is`) },
    { content: 'Un-approve', onAction: () => act(() => cacApi.unapprove(ids), (r) => `Un-approved ${r.unapproved} products`) },
    { content: 'Analyze with AI (batch, half price)', onAction: () => act(() => cacApi.analyzeProducts(ids, false, true), (r) => `Queued batched AI analysis of ${queuedText(ids, r)} (only what is unsettled; results within hours)${skippedText(r)}`) },
    { content: 'Analyze with AI (now)', onAction: () => act(() => cacApi.analyzeProducts(ids, false), (r) => `Queued AI analysis of ${queuedText(ids, r)} (only what is unsettled)${skippedText(r)}${r.kicked ? ' · running now' : ''}`) },
    { content: 'Force AI on selected', onAction: () => act(() => cacApi.analyzeProducts(ids, true), (r) => `Queued forced AI analysis of ${queuedText(ids, r)} (every layer, fresh)${skippedText(r)}${r.kicked ? ' · running now' : ''}`) },
    { content: 'Write to Shopify…', onAction: () => setWriting(true), disabled: !allApproved },
  ].map((a) => ({ ...a, disabled: a.disabled || busy }));

  const sortIndex = Math.max(0, SORT_COLS.indexOf(state.sort));
  const pages = Math.max(1, Math.ceil(data.total / PER_PAGE));
  const page = Number(state.page) || 1;
  const showing = { from: data.total ? (page - 1) * PER_PAGE + 1 : 0, to: Math.min(data.total, page * PER_PAGE) };

  return (
    <BlockStack gap="300">
      {state.run && <RunBanner runId={state.run} total={data.total} onClear={() => set({ run: '' })} />}
      <Card><ReviewFilters state={state} set={set} facets={dictionary} /></Card>
      {error && <Banner tone="critical" onDismiss={() => setError('')}><p>{error}</p></Banner>}
      {refused && (
        <Banner tone="critical" title={refused.detail} onDismiss={() => setRefused(null)}>
          <p>These products are still unresolved. Nothing was approved; open them to fix, or un-select them.</p>
          <List>{refused.ids.map((id) => <List.Item key={id}>{titleOf(id)}</List.Item>)}</List>
        </Banner>
      )}
      {ids.length > 0 && !allApproved && <Text as="p" tone="subdued">Write is enabled once every selected row is approved.</Text>}
      <Card padding="0">
        <IndexTable resourceName={{ singular: 'product', plural: 'products' }} itemCount={data.rows.length}
          selectedItemsCount={allResourcesSelected ? 'All' : selectedResources.length} onSelectionChange={handleSelectionChange}
          promotedBulkActions={bulkActions} sortable={SORT_COLS.map((c) => !!c)} sortColumnIndex={sortIndex}
          sortDirection={state.dir === 'asc' ? 'ascending' : 'descending'}
          onSort={(i, dir) => set({ sort: SORT_COLS[i], dir: dir === 'ascending' ? 'asc' : 'desc' })}
          loading={loading}
          headings={[{ title: 'Product' }, { title: 'Vendor' }, { title: 'Category: current → proposed' }, { title: 'Category metafields' }, { title: 'AI' }, { title: 'Facets', alignment: 'end' },
            { title: 'Conflicts', alignment: 'end' }, { title: 'Certainty', alignment: 'end' }, { title: 'Status' }, { title: 'Updated' }]}
          emptyState={<Box padding="800"><InlineStack align="center">{loading ? <Spinner accessibilityLabel="Loading" /> : <Text as="p" tone="subdued">No products match.</Text>}</InlineStack></Box>}>
          {data.rows.map((r, i) => (
            <IndexTable.Row id={String(r.product_id)} key={r.product_id} position={i} selected={selectedResources.includes(String(r.product_id))}
              onClick={() => navigate(productPath(r.product_id, search))}>
              <IndexTable.Cell>
                <InlineStack gap="300" blockAlign="center" wrap={false}>
                  <Thumbnail source={r.image_url || ImageIcon} alt="" size="small" />
                  <BlockStack gap="0">
                    <Text as="span" fontWeight="semibold" truncate>{r.title}</Text>
                    <Text as="span" variant="bodySm" tone="subdued">{r.vendor}</Text>
                  </BlockStack>
                </InlineStack>
              </IndexTable.Cell>
              <IndexTable.Cell><Text as="span">{r.vendor}</Text></IndexTable.Cell>
              <IndexTable.Cell>
                {r.category_changed
                  ? <BlockStack gap="0">
                      <Text as="span" variant="bodySm" tone="subdued" textDecorationLine="line-through">{fullPath(r.category_current_name)}</Text>
                      <Text as="span" variant="bodySm" fontWeight="semibold">→ {fullPath(r.category_proposed_name)}</Text>
                    </BlockStack>
                  : <InlineStack gap="200" blockAlign="center"><Text as="span" variant="bodySm" fontWeight="semibold">{fullPath(r.category_current_name)}</Text><Badge>kept</Badge></InlineStack>}
              </IndexTable.Cell>
              <IndexTable.Cell>
                {(r.std_facets || []).length
                  ? <InlineStack gap="100" wrap>{reviewChips(r.std_facets).map((c) => <Badge key={c.key} tone={c.warn ? 'warning' : undefined}>{c.text}</Badge>)}</InlineStack>
                  : <Text as="span" tone="subdued">—</Text>}
              </IndexTable.Cell>
              <IndexTable.Cell>{aiBadge(r)}</IndexTable.Cell>
              <IndexTable.Cell><Text as="span" alignment="end" numeric>{r.facets_set}</Text></IndexTable.Cell>
              <IndexTable.Cell>{r.conflict_count ? <InlineStack align="end"><Badge tone="critical">{String(r.conflict_count)}</Badge></InlineStack> : <Text as="span" alignment="end" tone="subdued">—</Text>}</IndexTable.Cell>
              <IndexTable.Cell><InlineStack align="end"><CertaintyPill value={r.certainty} /></InlineStack></IndexTable.Cell>
              <IndexTable.Cell><StatusBadge status={r.review_status} /></IndexTable.Cell>
              <IndexTable.Cell><Text as="span" tone="subdued">{updated(r.updated_at)}</Text></IndexTable.Cell>
            </IndexTable.Row>
          ))}
        </IndexTable>
        <Box padding="300" borderBlockStartWidth="025" borderColor="border">
          <InlineGrid columns={['oneHalf', 'oneHalf']} alignItems="center">
            <Text as="span" tone="subdued">Showing {showing.from}–{showing.to} of {data.total.toLocaleString()} products</Text>
            <InlineStack align="end">
              <Pagination hasPrevious={page > 1} hasNext={page < pages} onPrevious={() => set({ page: page - 1 })} onNext={() => set({ page: page + 1 })} />
            </InlineStack>
          </InlineGrid>
        </Box>
      </Card>
      {pickingCategory && <CategoryPicker onClose={() => setPickingCategory(false)} onPick={pickCategory} />}
      {writing && (
        <WriteModal ids={ids} titleOf={titleOf} onClose={() => setWriting(false)}
          onDone={({ written, failed, dry }) => {
            setWriting(false); clearSelection(); reload();
            notify(`Wrote ${written.length} products to Shopify (${failed.length} failed)${dry ? ' (dry run)' : ''}`, failed.length > 0);
          }} />
      )}
    </BlockStack>
  );
}
